/**
 * Toaster — global success/error/info feedback for mutations.
 *
 * Built on Radix's accessible Toast primitive so screen readers
 * announce transient feedback correctly, with a small Zustand store
 * holding the queue so any call-site can fire a toast without lifting
 * a provider prop down the tree. `<Toaster />` mounts once in the
 * AppShell; `useToast()` is callable from any component.
 *
 * Variants map to the design tokens: success → success, error → danger,
 * info → border (neutral). Toasts auto-dismiss after 4s; errors stay
 * visible for 7s because they usually bring an action with them.
 */

import * as Toast from '@radix-ui/react-toast';
import type { ReactNode } from 'react';
import { create } from 'zustand';

import { cn } from '@/utils/cn';

export type ToastVariant = 'success' | 'error' | 'info';

interface ToastRecord {
  id: string;
  title: string;
  description?: string;
  variant: ToastVariant;
  duration: number;
  action?: { label: string; onClick: () => void };
}

interface ToastStore {
  toasts: ToastRecord[];
  push: (input: Omit<ToastRecord, 'id' | 'duration'> & { duration?: number }) => void;
  dismiss: (id: string) => void;
}

const DEFAULT_DURATIONS: Record<ToastVariant, number> = {
  success: 4000,
  info: 4000,
  error: 7000,
};

const useToastStore = create<ToastStore>((set) => ({
  toasts: [],
  push: (input) =>
    set((s) => ({
      toasts: [
        ...s.toasts,
        {
          id:
            typeof crypto !== 'undefined' && 'randomUUID' in crypto
              ? crypto.randomUUID()
              : `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          duration: input.duration ?? DEFAULT_DURATIONS[input.variant],
          ...input,
        },
      ],
    })),
  dismiss: (id) =>
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

export function useToast() {
  const push = useToastStore((s) => s.push);
  return {
    success: (title: string, description?: string) =>
      push({ title, variant: 'success', ...(description ? { description } : {}) }),
    error: (title: string, description?: string) =>
      push({ title, variant: 'error', ...(description ? { description } : {}) }),
    info: (title: string, description?: string) =>
      push({ title, variant: 'info', ...(description ? { description } : {}) }),
    /** Escape hatch for richer payloads (e.g. action buttons). */
    push,
  };
}

export function Toaster() {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);

  return (
    <Toast.Provider swipeDirection="right" duration={4000}>
      {toasts.map((t) => (
        <Toast.Root
          key={t.id}
          open
          duration={t.duration}
          onOpenChange={(open) => {
            if (!open) dismiss(t.id);
          }}
          className={cn(
            'pointer-events-auto rounded-lg border bg-surface p-4 shadow-float',
            'data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)]',
            'data-[swipe=cancel]:translate-x-0 data-[swipe=cancel]:transition-transform',
            'data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)]',
            variantBorder(t.variant),
          )}
          data-testid={`toast-${t.variant}`}
        >
          <div className="flex items-start gap-3">
            <ToastIcon variant={t.variant} />
            <div className="flex-1 min-w-0">
              <Toast.Title className="text-sm font-semibold text-ink">
                {t.title}
              </Toast.Title>
              {t.description ? (
                <Toast.Description className="mt-1 text-sm text-ink-muted break-words">
                  {t.description}
                </Toast.Description>
              ) : null}
            </div>
            {t.action ? (
              <Toast.Action
                altText={t.action.label}
                onClick={t.action.onClick}
                className="shrink-0 rounded-md px-2 py-1 text-xs font-medium text-spark hover:bg-spark-soft"
              >
                {t.action.label}
              </Toast.Action>
            ) : null}
            <Toast.Close
              aria-label="Close"
              className="shrink-0 rounded-md px-1 text-xs text-ink-subtle hover:text-ink"
            >
              ×
            </Toast.Close>
          </div>
        </Toast.Root>
      ))}
      <Toast.Viewport
        className={cn(
          'fixed bottom-4 end-4 z-50 flex w-96 max-w-[calc(100vw-2rem)]',
          'flex-col gap-2 outline-none',
        )}
      />
    </Toast.Provider>
  );
}

function variantBorder(variant: ToastVariant): string {
  switch (variant) {
    case 'success':
      return 'border-success/40';
    case 'error':
      return 'border-danger/40';
    case 'info':
      return 'border-border';
  }
}

function ToastIcon({ variant }: { variant: ToastVariant }): ReactNode {
  const base = 'mt-0.5 h-4 w-4 shrink-0 rounded-full';
  switch (variant) {
    case 'success':
      return <span aria-hidden className={cn(base, 'bg-success')} />;
    case 'error':
      return <span aria-hidden className={cn(base, 'bg-danger')} />;
    case 'info':
      return <span aria-hidden className={cn(base, 'bg-spark')} />;
  }
}
